import { BaseEntity } from "../base-entity";
import { MatterType } from "./matter-type";
import {
  TransactionTypeValue,
  UnityConnectTransactionType,
} from "./unity-connect-transaction-type";
import { Utils } from "../utils";
import { Cirf, CirfType } from "../../cirf/cirf";
import { SharedDocumentsPackage } from "../shared-documents-package";
import { Referral } from "../../referral/referral";
import { CirfMatterData } from "../../cirf/cirf-matter-data";
import { MatterInfo } from "../matter-info";
import { DOC_STATUS_IS_NEW, DOC_STATUS_IS_UPDATED } from "../shared-document";
import { DocumentsPackageHistoryRecord } from "../shared-documents-package-history";

export class UnityConnectTransaction extends BaseEntity {
  transactionType: UnityConnectTransactionType;
  propertyAddress: string;
  lawFirm: string;
  status: string;
  matterId: number;
  matterType: MatterType;
  matterTypeName: string; // this will have all matter types including custom matter types
  matterCloseDate: string;
  matterAddressOrDescription: string;
  client: string;
  date: string;
  guid: string;
  id: number;
  lastUpdatedTimeStamp: number;
  // It is used to display date on UI & Used for sorting & will be
  // holding different values for different entities.
  transactionDate: number;
  //expanded: boolean = false;
  //values that can be used to search for this record
  searchTerms: string[] = [];
  linkId: number;
  cirfType: CirfType;
  matterInfo: MatterInfo;
  // UI only - Flag will be true in case the referral created by a Unity Connect User is added as Participant in Matter
  referralAddedAsParticipant: boolean;

  historyRecord: DocumentsPackageHistoryRecord; //For UI only
  hasUnopenedDocuments: boolean; //For UI only check Shared Documents if there unopened documents
  get transactionTypeStr(): string {
    switch (this.transactionType) {
      case "CIRF":
        return "Client Information Form";
      case "REFERRAL":
        return "Referral";
      case "SHARED_DOCUMENT_PACKAGE":
        return "Shared Documents";
      default:
        return "";
    }
  }

  get propertyAddressShort(): string {
    return Utils.truncateStringForDisplay(this.propertyAddress, 30, "...");
  }

  get lawFirmShort(): string {
    return Utils.truncateStringForDisplay(this.lawFirm, 30, "...");
  }

  get statusDisplay(): string {
    if (this.status === "CANCELLED") {
      return "Cancelled";
    } else if (this.status === "EXPIRED") {
      return "Expired";
    } else if (this.status === "INPROGRESS") {
      return "In Progress";
    } else if (this.status === "IMPORTINPROGRESS") {
      return "Submitted";
    } else if (this.status === "OUTSTANDING") {
      return "Requested";
    } else if (this.status === "PROCESSED") {
      return "Processed";
    } else if (this.status === "SUBMITTED") {
      return "Submitted";
    } else if (this.status === "DRAFT") {
      return "Draft";
    } else if (this.status === "ACCEPTED") {
      return "Accepted";
    }
    return this.status;
  }

  get historyItemRead(): string {
    return this.historyRecord
      ? this.historyRecord.packageHistoryItemRead
        ? "Read"
        : "Unread"
      : "";
  }

  static fromCirf(cirf: Cirf): UnityConnectTransaction {
    const uct: UnityConnectTransaction = new UnityConnectTransaction();
    uct.transactionType = "CIRF";
    uct.propertyAddress = cirf.propertyAddress;
    uct.lawFirm = cirf.lawFirm;
    uct.status = cirf.status;
    uct.matterType = cirf.matterType;
    uct.matterTypeName = cirf.matterTypeName;
    uct.client = "";
    uct.lastUpdatedTimeStamp = cirf.lastUpdatedTimeStamp;
    uct.transactionDate = cirf.lastUpdatedTimeStamp;
    uct.guid = cirf.guid;
    uct.matterId = cirf.matterId;
    uct.matterCloseDate = cirf.matterCloseDate;
    uct.matterAddressOrDescription = cirf.matterAddressOrDescription;
    uct.cirfType = cirf.cirfType;
    if (cirf.matterAddressOrDescription) {
      uct.searchTerms.push(cirf.matterAddressOrDescription);
    }
    if (cirf.status !== "SUBMITTED") {
      let cirfMatterData = new CirfMatterData(JSON.parse(cirf.formData));
      if (Utils.isNotNullorUndefined(cirfMatterData.cirfClients)) {
        cirfMatterData.cirfClients.forEach((c) => {
          uct.searchTerms.push(c.fullName);
          c.addresses.forEach((a) =>
            Utils.isNotNullorUndefined(a.fullAddress)
              ? uct.searchTerms.push(a.fullAddress)
              : null
          );
        });
      }
    }
    return uct;
  }

  static fromShared(
    sdp: SharedDocumentsPackage,
    sdph?: DocumentsPackageHistoryRecord
  ): UnityConnectTransaction {
    const uct: UnityConnectTransaction = new UnityConnectTransaction();
    uct.historyRecord = sdph;
    uct.transactionType = "SHARED_DOCUMENT_PACKAGE";
    uct.propertyAddress = sdp?.matterInfo?.propertyAddress?.addressLine1;
    uct.lawFirm = sdp.matterInfo?.lawFirm;
    uct.status = "Shared";
    uct.matterType = sdp?.matterInfo?.matterType;
    uct.matterTypeName = sdp?.matterInfo?.matterTypeName;
    uct.client = "";
    uct.lastUpdatedTimeStamp = uct.historyRecord
      ? uct.historyRecord.lastUpdatedTimeStamp
      : sdp.lastUpdatedTimeStamp;
    uct.transactionDate = uct.historyRecord
      ? uct.historyRecord.lastUpdatedTimeStamp
      : sdp.lastUpdatedTimeStamp;
    uct.guid = sdp.guid;
    uct.matterId = sdp.matterId;
    uct.matterCloseDate = sdp.matterInfo?.closingDate;
    uct.matterAddressOrDescription = sdp.matterAddressOrDescription;
    if (Utils.isNotNullorUndefined(sdp.matterAddressOrDescription)) {
      uct.searchTerms.push(sdp.matterAddressOrDescription);
    }

    if (uct.historyRecord) {
      uct.hasUnopenedDocuments =
        sdp &&
        sdp.documents &&
        sdp.documents.length > 0 &&
        sdp.documents.some(
          (item) =>
            (item.docStatus === DOC_STATUS_IS_NEW ||
              item.docStatus === DOC_STATUS_IS_UPDATED) &&
            uct.historyRecord.documentDetails &&
            uct.historyRecord.documentDetails.find(
              (doc) => doc.documentName === item.name && !doc.read
            )
        );
    } else {
      uct.hasUnopenedDocuments =
        sdp &&
        sdp.documents &&
        sdp.documents.length > 0 &&
        sdp.documents.some(
          (item) =>
            item.docStatus === DOC_STATUS_IS_NEW ||
            item.docStatus === DOC_STATUS_IS_UPDATED
        );
    }
    /*if(sdp && sdp.documents && sdp.documents.length> 0 && sdp.documents.some(item => item.docStatus == DOC_STATUS_IS_NEW || item.docStatus == DOC_STATUS_IS_UPDATED)){
            uct.hasUnopenedDocuments = true;
        }*/

    return uct;
  }

  static fromReferrals(referral: Referral): UnityConnectTransaction {
    const uct: UnityConnectTransaction = new UnityConnectTransaction();
    uct.transactionType = "REFERRAL";

    // in case syncing with matter is needed
    // uct.propertyAddress = referral?.propertyAddress;
    // uct.matterType = referral?.matterType;
    uct.propertyAddress = referral?.property;
    uct.matterType = <MatterType>referral?.referralForm?.transactionType;
    uct.matterTypeName = referral.matterTypeName
      ? referral.matterTypeName
      : uct.matterType;

    uct.lawFirm = referral?.lawFirm;
    uct.client = referral?.client;
    uct.lastUpdatedTimeStamp = referral?.lastUpdatedTimeStamp;
    uct.transactionDate = referral?.submittedOn
      ? referral?.submittedOn
      : referral?.lastUpdatedTimeStamp;
    uct.status = referral?.status;
    uct.guid = referral?.id.toString();
    uct.matterId = referral.assignedMatterId;
    uct.matterCloseDate = referral.matterCloseDate;
    uct.referralAddedAsParticipant = referral.referralAddedAsParticipant;
    if (referral.assignedMatterId) {
      uct.matterAddressOrDescription = referral.matterAddressOrDescription;
    } else {
      uct.matterAddressOrDescription =
        referral?.referralForm?.propertyAddress?.fullAddress.replace(/,/g, "");
    }
    if (referral?.referralForm?.propertyAddress?.fullAddress) {
      uct.searchTerms.push(
        referral?.referralForm?.propertyAddress?.fullAddress.replace(/,/g, "")
      );
    }
    if (referral?.status !== "SUBMITTED") {
      referral?.referralForm?.referredClients.forEach((client) => {
        if (Utils.isNotNullorUndefined(client.fullName)) {
          uct.searchTerms.push(client.fullName);
        }
        if (Utils.isNotNullorUndefined(client?.address?.fullAddress)) {
          uct.searchTerms.push(client.address.fullAddress);
        }
      });
    }
    return uct;
  }

  static fromParticipatingMatter(
    matterInfo: MatterInfo
  ): UnityConnectTransaction {
    const uct: UnityConnectTransaction = new UnityConnectTransaction();
    uct.transactionType = "PARTICIPATING_MATTER";
    uct.propertyAddress = matterInfo?.propertyAddress?.addressLine1;
    uct.lawFirm = matterInfo?.lawFirm;
    uct.status = matterInfo?.matterStatus;
    uct.matterType = matterInfo?.matterType;
    uct.matterTypeName = matterInfo?.matterTypeName;
    uct.client = "";
    uct.guid = matterInfo?.id.toString();
    uct.matterId = matterInfo?.id;
    uct.matterCloseDate = matterInfo?.closingDate;
    uct.matterAddressOrDescription = matterInfo.matterAddressOrDescription;
    if (Utils.isNotNullorUndefined(matterInfo.matterAddressOrDescription)) {
      uct.searchTerms.push(matterInfo.matterAddressOrDescription);
    }
    uct.linkId = matterInfo.linkId;
    uct.matterInfo = new MatterInfo(matterInfo);
    return uct;
  }

  isTransactionUnProccesedReferral(): boolean {
    return (
      this.transactionType === TransactionTypeValue.referral && !this.matterId
    );
  }

  isReferralAddedAsParticipant(): boolean {
    return (
      this.transactionType === TransactionTypeValue.referral &&
      !!this.referralAddedAsParticipant
    );
  }

  isParticipantCirf(): boolean {
    return (
      this.transactionType == TransactionTypeValue.cirf &&
      this.cirfType == "PARTICIPANT"
    );
  }

  isMatterCirf(): boolean {
    return (
      this.transactionType == TransactionTypeValue.cirf &&
      this.cirfType == "MATTER"
    );
  }

  isCancelled(): boolean {
    return this.status == "CANCELLED";
  }

  get isAccepted(): boolean {
    return this.status == "ACCEPTED";
  }
}
